<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { ContentComp, TabComp, AlertMessage, SavedChangesMessage, UnsavedChangesMessage, InputBool } from '@/components';
import { useAccessStore, useEventCommonStoreRef, useEventStore, useToastStore, useUtilStore } from '@/stores';
import { TabContainer, TabDefinition, type ITabContainer } from '@/objects/Tabs';
import {
    Category,
    Group,
    Participant,
    RaceContestant,
    RaceContestantDetails,
    SetContestantStatusCancelledRequest,
    ContestantFavorite,
} from '@/objects/generated';
import { useLoaders, useTaskLoader } from '@/composeables/loaders';
import { $tm, featureIcons } from '@/.';
import type DetailViewExpose from '@/objects/DetailViewExpose';
import type { FieldAlertMessage } from '@/objects';
import ContestantGeneral from './ContestantGeneral.vue';
import { debouncedWatch } from '@vueuse/core';
import ContestantRankCategories from './ContestantRankCategories.vue';
import ContestantTimes from './ContestantTimes.vue';
import ContestantTracking from './ContestantTracking.vue';
import ContestantTimepenalty from './ContestantTimepenalty.vue';
import ContestantRelay from './ContestantRelay.vue';
import ContestantPoints from './ContestantPoints.vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import { marked } from 'marked';
import MissingCategoryMessage from '@/components/Alert/MissingBaseItem.vue';
import ContestantShootings from './ContestantShootings.vue';
import WellKnown from '@/objects/generated/static/WellKnown';
import ParticipantIDsView from '../Participant/ParticipantIDsView.vue';
import ContestantAddress from './ContestantAddress.vue';
import { findAvailableCategoriesForGenderAndBirthdayInCurrentEvent } from '@/utilities/categoryutils';
import { BaseObjDao, Quick3Dao } from '@/dao';
import { useRelay } from '@/composeables/relay';
import { useDialog } from '@/composeables';
import ContestantFavoriteView from '../Speaker/ContestantFavoriteView.vue';
import ContestantFavoriteDetails from './ContestantFavoriteDetails.vue';
import { formatDateDefault, isSame } from '@/utilities/dateutils';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';

const eventStore = useEventStore();

/**
 * The props for this view
 */
const props = withDefaults(
    defineProps<{
        /**
         * The ID of the race contestant we are showing
         */
        id: number;

        /**
         * The participant we want to merge into the contestant
         */
        participant?: Participant;

        /**
         * The race we want to create the raceContestant for
         */
        raceId?: number;

        /**
         * If this is set, the participantsearch will be opened when the view is shown
         */
        openParticipantSearch?: boolean;

        /**
         * Pre-fill the bib number when creating a new contestant
         */
        preFillBib?: number;

        /**
         * The starttime to set on the default raceContestant when creating a new contestant
         */
        prefillStartTime?: Date;
    }>(),
    {
        openParticipantSearch: true,
    },
);

const modelValue = defineModel<RaceContestantDetails | null>({
    required: false,
    default: null,
});

const { Categories, Groups, Stations, Races } = useEventCommonStoreRef();

// We need to check if this is a relay event
const { IsRelayEvent } = useRelay();

// If the ID is = 0 we are creating a new contestant
const contestant = ref<RaceContestantDetails | null>(null);

// reference to the general view so we can call dataUpdated when we save
const contestantGeneralView = ref<ComponentExposed<typeof ContestantGeneral> | null>(null);

const participantIDsView = ref<ComponentExposed<typeof ParticipantIDsView> | null>(null);

// Reference to the contestant address view
const contestantAddressView = ref<ComponentExposed<typeof ContestantAddress> | null>(null);

// We show the success message when the contestant is saved
const allChangesSaved = ref<boolean>(false);

// A ref to the alert message component for errors that are not shown in the toast
const detailComp = ref<{ messages: FieldAlertMessage[] } | null>(null);

// A ref to the contestant points component
const contestantPoints = ref<InstanceType<typeof ContestantPoints> | null>(null);

/**
 * Helper to see if the original value has changed or not
 * @remarks this is only for the main v-model RaceContestantDetails
 */
const contestantChanged = computed(() => (contestant.value?.EntityState ?? 'Unchanged') !== 'Unchanged');

/**
 * If there is any changes in the points tab we need to show a save button
 */
const contestantPointsChanged = ref<boolean>(false);

/**
 * If there is any changes we want to show the save button
 */
const computedChanged = computed(() => contestantPointsChanged.value || contestantChanged.value);

const { AddLoader, LoadState: loadContestantState } = useTaskLoader();
useLoaders(AddLoader, ['STATIONS', 'STATIONROLES', 'RACES', 'LICENCES', 'CATEGORIES', 'GROUPS', 'ACTIVETIMES']);

/**
 * Helper function for getting the category that matches for a participant
 * @param participant
 */
const getCategoryForParticipant = (participant: Participant | null): Category | null => {
    // we select race for first added raceContestant if there is one
    const raceId = contestant.value
        ? [...contestant.value.RaceContestants].sort((a, b) => a.ID - b.ID).find((x) => x.EntityState != 'Deleted')?.RaceID
        : undefined;

    const matchingCategories = findAvailableCategoriesForGenderAndBirthdayInCurrentEvent({
        gender: participant?.Gender ?? undefined,
        age: participant?.EventAge ?? participant?.Age ?? undefined,
        raceId: raceId,
    });

    if (matchingCategories.length) {
        return Categories.value.find((c) => c.ID === matchingCategories[0]) ?? null;
    }
    return null;
};

const contestantFavoriteModel = ref<ContestantFavorite | null>(null);

/**
 * Returns the group that the category belongs to
 * If there are multiple groups, it will return the first one found
 * @param category
 */
const getGroupForCategory = (category: Category | null): Group | null => {
    // We need to find the Group that the category belongs to
    let groupIds = category?.CategoryGroupConnection?.map((x) => x.GroupID) ?? [];
    return Groups.value.find((x) => groupIds.includes(x.ID) && (props.raceId ? x.RaceID === props.raceId : true)) ?? null;
};

AddLoader(async () => {
    // We create a new contestant object if the ID is 0
    if (props.id <= 0) {
        // We try and find a category for the participant
        const category = props.participant ? getCategoryForParticipant(props.participant) : null;

        // We try and find a group for the category
        let group = getGroupForCategory(category);

        if (!group) {
            group = Groups.value.at(0) ?? null;
        }

        contestant.value = RaceContestantDetails.FromJsValues({
            EntityState: 'Added',
            EventID: eventStore.eventID,
            ID: useUtilStore().nextFakeId(),
            ContestantID: useUtilStore().nextFakeId(),
            // If we have a category we set the category
            CategoryID: category?.ID ?? null,
            // If we have a participany, add participant data
            ParticipantID: props.participant?.ID,
            Firstname: props.participant?.Firstname ?? '',
            Surname: props.participant?.Surname ?? '',
            Gender: props.participant?.Gender,
            Birthday: props.participant?.Birthday,
            Address1: props.participant?.Address1,
            Address2: props.participant?.Address2,
            City: props.participant?.City,
            AddressCountryISO2: props.participant?.AddressCountryISO2,
            Email: props.participant?.Email,
            Phone: props.participant?.Phone,
            NationalityCountryID: props.participant?.NationalityCountryID,

            EventAge: props.participant?.EventAge,

            // If we have category and group, we add a raceContestant
            RaceContestants:
                !category || !group
                    ? []
                    : [
                          RaceContestant.FromJsValues({
                              EntityState: 'Added',
                              EventID: eventStore.eventID,
                              ID: useUtilStore().nextFakeId(),
                              GroupID: group.ID,
                              RaceID: group.RaceID,
                              CategoryID: category.ID,
                              StartingTime: props.prefillStartTime ? props.prefillStartTime : undefined,
                          }),
                      ],
        });
    } else {
        contestant.value = await BaseObjDao.LoadOrCreateSingle(RaceContestantDetails, props.id, eventStore.eventID);

        contestantFavoriteModel.value = await BaseObjDao.LoadOrCreateSingle(ContestantFavorite, props.id, eventStore.eventID);
    }
    modelValue.value = contestant.value;
}, 'contestant.loading_contestant');

const _rankingCategoryCount =
    contestant.value?.RaceContestants.flatMap((rc) => rc.RankingCategories)
        .filter((x) => x.EntityState != 'Deleted')
        .length?.toString() ?? '';
const rankingCatecoryCount = ref<string>(_rankingCategoryCount == '0' ? '' : _rankingCategoryCount);

const { HasFunctionality } = useAccessStore();

/**
 * We check if we should show the shooting details tab
 */
const showShootingDetails = computed(() => {
    // This is the proper way to check if we have shooting functionality but this is not backwards compatible
    if (HasFunctionality('Shooting')) {
        return true;
    }

    // Then we need to check if there is any stations with the type shooting
    return Stations.value.some(
        (x) =>
            x.StationTypeID == WellKnown.STATIONTYPE_SHOOTING_PRONE ||
            x.StationTypeID == WellKnown.STATIONTYPE_SHOOTING_STAND ||
            x.StationTypeID == WellKnown.STATIONTYPE_PENALTYLAP ||
            x.StationTypeID == WellKnown.STATIONTYPE_SHOOTING_CONTROL,
    );
});

const tabs = ref<ITabContainer>(new TabContainer());
tabs.value.AddTab(
    new TabDefinition(
        'general',
        'contestant.general',
        'fa-user-pen',
        undefined,
        undefined,
        undefined,
        computed(() => (contestantGeneralView.value?.messages?.filter((x) => x.alertMessage?.type == 'danger').length ?? 0) > 0),
        computed(() => (contestantGeneralView.value?.messages?.filter((x) => x.alertMessage?.type == 'warning').length ?? 0) > 0),
    ),
);
tabs.value.AddTab(
    new TabDefinition(
        'contact',
        'contestant.contact',
        'fa-address-card',
        undefined,
        undefined,
        undefined,
        computed(() => (contestantAddressView.value?.messages?.filter((x) => x.alertMessage?.type == 'danger').length ?? 0) > 0),
        computed(() => (contestantAddressView.value?.messages?.filter((x) => x.alertMessage?.type == 'warning').length ?? 0) > 0),
    ),
);
tabs.value.AddTab(new TabDefinition('favorite', 'contestant.favorite', 'fa-star'));
tabs.value.AddTab(new TabDefinition('ids', 'contestant.participantids', 'fa-tags'));
tabs.value.AddTab(new TabDefinition('rankingcategories', 'contestant.rankingcategories', featureIcons.CATEGORY, rankingCatecoryCount));
if (IsRelayEvent.value) {
    tabs.value.AddTab(new TabDefinition('relay', 'contestant.relay', 'fa-users'));
}
tabs.value.AddTab(new TabDefinition('tracking', 'contestant.tracking', featureIcons.TRACKING));

let isRaceContestantTabsAdded = false;

watch(IsRelayEvent, (nv) => {
    if (nv) {
        if (!tabs.value.HasTab('relay')) {
            tabs.value.AddTab(new TabDefinition('relay', 'contestant.relay', 'fa-users'), 2);
        }
    } else {
        tabs.value.RemoveTab('relay');
    }
});

/**
 * we watch for changes in the contestant object
 * This can be a deep watch since it is only one object so it doesnt matter much. That way we will also pick up changes in the nested objects
 */
debouncedWatch(
    contestant,
    (nv, ov) => {
        const rankingCategoriesCount =
            contestant.value?.RaceContestants.flatMap((rc) => rc.RankingCategories)
                .filter((x) => x.EntityState != 'Deleted')
                .length?.toString() ?? '';
        rankingCatecoryCount.value = rankingCategoriesCount == '0' ? '' : rankingCategoriesCount;

        // Check if we have any raceContestant and then add the tabs
        if (nv != null && nv.RaceContestants?.length > 0 && isRaceContestantTabsAdded === false) {
            tabs.value.AddTab(new TabDefinition('times', 'contestant.times', 'fa-clock', undefined, 'right'), undefined, false);
            tabs.value.AddTab(new TabDefinition('points', 'contestant.points', 'fa-hashtag'), undefined, false);
            tabs.value.AddTab(new TabDefinition('penalty', 'contestant.penalty', 'fa-stopwatch'), undefined, false);

            // TODO: We need to check if there are shooting in this race type
            if (showShootingDetails.value) {
                tabs.value.AddTab(new TabDefinition('shooting', 'contestant.shootings', 'fa-bullseye'), undefined, false);
            }
            isRaceContestantTabsAdded = true;
        }
    },
    { deep: true, debounce: 300 },
);

const saveErrorMessage = ref<string | null>(null);

/**
 * We save the details to the backend
 * @returns The IDs of the saved objects we want to update in the list. Since the list is based on the IDs of RaceContestants we will return all the IDs of the RaceContestants
 */
const saveDetails = async (
    saveStateLoader?: (loader: () => Promise<void>, key: string, icon: string | null, singleloader?: boolean) => Promise<void>,
) => {
    if (contestant.value == null) {
        return [];
    }

    // check if we have chnaged the group and ask if we should change starttime
    if (
        contestant.value.RaceContestants.length == 1 &&
        contestant.value.RaceContestants[0].EntityState == 'Modified' &&
        contestant.value.RaceContestants[0].IsPropertyChanged('GroupID')
    ) {
        // we only ask if the group has a starttime and it is different from the current starttime
        const st = Groups.value.find((g) => g.ID == contestant.value?.RaceContestants[0].GroupID)?.StartTime;

        if (st && !isSame(st, contestant.value.RaceContestants[0].StartingTime)) {
            const dialogInstance = await useDialogBuilder()
                .withTitle('contestant.change_group')
                .withBodyText('contestant.change_group_starttime_question', { starttime: formatDateDefault(st, 'TIME') })
                .withYesButton({
                    action: async () => {
                        return true;
                    },
                })
                .withNoButton({
                    action: async () => {
                        return false;
                    },
                })
                .Open();
            const setStart = await dialogInstance.closePromise;
            if (setStart) {
                contestant.value.RaceContestants[0].StartingTime = st;
            }
        }
    }

    saveErrorMessage.value = null;

    let saveSuccess = true;

    // if saveStateLoader is not provided we use our own
    const saveStateLoaderFinal = saveStateLoader ?? useTaskLoader().AddLoader;

    if (props.id == 0 || contestantChanged.value) {
        await saveStateLoaderFinal(
            async () => {
                try {
                    if (contestant.value == null) {
                        return;
                    }

                    const resp = await BaseObjDao.Save(RaceContestantDetails, eventStore.eventID, [contestant.value!]);

                    if (resp == null || resp.IDS == null || resp.IDS.length === 0) {
                        throw new Error('general.error');
                    }

                    // We need to reload the contestant object and update the data
                    contestant.value = await BaseObjDao.LoadOrCreateSingle(RaceContestantDetails, resp?.IDS[0], eventStore.eventID);

                    contestantGeneralView.value?.dataUpdated();
                    participantIDsView.value?.dataUpdated();
                } catch (ex) {
                    handleSaveError(ex as Error);
                    saveSuccess = false;
                }
            },
            '',
            null,
        );
    }

    if (contestantPointsChanged.value) {
        await saveStateLoaderFinal(
            async () => {
                try {
                    await contestantPoints.value?.save();
                    contestantPointsChanged.value = false;
                } catch (ex) {
                    handleSaveError(ex as Error);
                    saveSuccess = false;
                }
            },
            '',
            null,
        );
    }

    if (!saveSuccess) {
        return [];
    }

    // Ask if we should delete orderlines, if we have avmeldt the contestant.
    // This does not check if we have changed the ContestantStatus so we get the question every time we save
    if (
        contestant.value.ContestantStatusID === WellKnown.CONTESTANTSTATUS_CANCELLED &&
        contestant.value.OrderNumber &&
        !contestant.value.InvoiceNumber
    ) {
        useDialog().YesNo({
            title: 'contestant.delete_order_lines_title',
            body: 'contestant.delete_order_lines',
            yesCallback: async () => {
                await saveStateLoaderFinal(
                    async () => {
                        try {
                            await Quick3Dao.DeleteContestantOrderLinesAndSetContestantStatusToCancelled(eventStore.eventID, [
                                SetContestantStatusCancelledRequest.FromJsValues({
                                    ContestantId: contestant.value!.ContestantID,
                                    Action: 'SetCancelledAndDeleteOrderLines',
                                }),
                            ]);
                        } catch (ex) {
                            console.error(ex);
                            useToastStore().addError('contestant.delete_order_lines_error');
                        }
                    },
                    '',
                    null,
                );
                return true;
            },
        });
    }

    allChangesSaved.value = true;

    // We reset the save message after 5 seconds this is to give us a smooth transition and fade the message out
    setTimeout(() => {
        allChangesSaved.value = false;
    }, 5000);

    return contestant.value?.RaceContestants.map((x) => x.ID) ?? [];
};

/**
 * We handle the error when saving the contestant
 * @param ex The error that was thrown
 */
const handleSaveError = (ex: Error | null) => {
    if (ex == null) {
        saveErrorMessage.value = null;
        return;
    }

    if (ex != null && ex?.cause == '409') {
        saveErrorMessage.value = marked.parse($tm(ex.message, '', true)).toString();
    } else {
        console.error(ex);
        useToastStore().addError(ex.toString());
    }
};

const computedTitle = computed(() => {
    if (contestant.value == null) {
        return '';
    }

    if (contestant.value.RelayTeamName != null && contestant.value.RelayTeamName.trim() != '') {
        return contestant.value.RelayTeamName.trim();
    }

    let namepart = `${contestant.value.Firstname} ${contestant.value.Surname}`;

    if (contestant.value.Bib != null && !isNaN(contestant.value.Bib)) {
        namepart = `${contestant.value.Bib}. ${namepart}`.trim();
    }

    return namepart.trim();
});

// Expose the view
defineExpose<DetailViewExpose<RaceContestantDetails> & { setError: (error: Error | null) => void }>({
    val: computed(() => contestant.value),
    title: computedTitle,
    changed: computedChanged,
    messages: computed(() => {
        let _messages = [];

        if (detailComp.value?.messages != null) {
            _messages.push(...detailComp.value.messages);
        }

        if (
            contestantGeneralView.value != null &&
            contestantGeneralView.value.messages != null &&
            contestantGeneralView.value.messages!.length > 0
        ) {
            _messages.push(...contestantGeneralView.value.messages!);
        }

        // We do the same with the contestantAddressView messages
        if (
            contestantAddressView.value != null &&
            contestantAddressView.value.messages != null &&
            contestantAddressView.value.messages!.length > 0
        ) {
            _messages.push(...contestantAddressView.value.messages!);
        }

        return _messages;
    }),
    save: saveDetails,

    /**
     * Set the error message to show in the detail view
     * @param error
     */
    setError: (error: Error | null) => handleSaveError(error),
});

onMounted(() => {
    if (props.preFillBib != null && contestant.value != null) {
        contestant.value.Bib = props.preFillBib;
    }
});
</script>
<template>
    <ContentComp
        :load-state="loadContestantState"
        :fill-content="true"
        :min-height="50"
    >
        <AlertMessage
            type="danger"
            :show-icon="false"
            v-if="saveErrorMessage != null"
            ><span v-html="$tm(saveErrorMessage, '', true)"></span
        ></AlertMessage>

        <MissingCategoryMessage
            text="category.missingcategory"
            buttontext="category.newcategory"
            page="categories"
            :icon="featureIcons.CATEGORY"
        />

        <UnsavedChangesMessage v-if="computedChanged && contestant != null && contestant.EntityState != 'Added'" />
        <SavedChangesMessage :visible="allChangesSaved" />

        <TabComp
            :tabs="tabs as TabContainer"
            :pinable="false"
            v-if="contestant != null"
            tab-body-classes="mt-3"
        >
            <template #tab-general>
                <ContestantGeneral
                    ref="contestantGeneralView"
                    v-model="contestant"
                    :is-relay-race="IsRelayEvent"
                    :open-participant-search="props.openParticipantSearch && !IsRelayEvent"
                    :race-id="raceId"
                    :prefill-start-time="props.prefillStartTime"
                />
            </template>
            <template #tab-contact>
                <ContestantAddress
                    ref="contestantAddressView"
                    v-model="contestant"
                />
            </template>
            <template #tab-favorite>
                <span>
                    <InputBool
                        v-model="contestant.IsFavorite"
                        class="mb-3 ms-2"
                        label="contestant.setfavorite"
                        tooltip="contestant.setfavoritetooltip"
                    ></InputBool>

                    <ContestantFavoriteDetails
                        v-model="contestant"
                        class="h-100"
                /></span>
            </template>
            <template #tab-times>
                <ContestantTimes v-model="contestant" />
            </template>
            <template #tab-shooting>
                <ContestantShootings
                    v-if="showShootingDetails"
                    v-model="contestant"
                />
            </template>
            <template #tab-rankingcategories><ContestantRankCategories v-model="contestant" /></template>
            <template #tab-relay>
                <ContestantRelay
                    v-if="IsRelayEvent"
                    v-model="contestant.ContestantID"
                />
            </template>
            <template #tab-tracking><ContestantTracking v-model="contestant" /> </template>
            <template #tab-points
                ><ContestantPoints
                    ref="contestantPoints"
                    v-model="contestant"
                    v-model:changed="contestantPointsChanged"
                />
            </template>
            <template #tab-penalty>
                <ContestantTimepenalty v-model="contestant" />
            </template>
            <template #tab-ids>
                <ParticipantIDsView
                    v-if="contestant.ParticipantIDs !== null"
                    ref="participantIDsView"
                    v-model="contestant.ParticipantIDs"
            /></template>
        </TabComp>
    </ContentComp>
</template>
